import { BaseOption } from 'components/forms/multiSelectField/MultiSelectField.types';
import useAppQuery from 'hooks/useAppQuery';
import useAppUser from 'hooks/useAppUser';
import { SelectableFeaturedProperty } from 'models/DbsSettings';
import API from 'services/API';
import { TAB_HTML_TAG } from 'utils/property/propertyUtils';
import { trimAndLimitCharactersNumber } from 'utils/stringUtils';
import { DBS_SETTINGS_URL } from '../useFetchDbsSettings';

const getPropertyOptions = (
  properties: SelectableFeaturedProperty[],
): BaseOption[] => {
  const groups = new Map<string, BaseOption[]>();

  properties.forEach(({ uid, name, hotelUid, hotelName }) => {
    if (!hotelUid) {
      groups.set(uid, [{ label: name, value: uid }]);
      return;
    }

    groups.set(hotelUid, [
      ...(groups.get(hotelUid) ?? [
        { label: hotelName ?? '', value: hotelUid, disabled: true },
      ]),
      {
        label: `${TAB_HTML_TAG} ${trimAndLimitCharactersNumber({
          text: name,
          limit: 45,
        })}`,
        value: uid,
      },
    ]);
  });

  return Array.from(groups.values())
    .sort(([a], [b]) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    )
    .flat();
};

const useFetchSelectablePropertyOptions = () => {
  const { agencyUid } = useAppUser();

  const { data, isInitialLoading, isError } = useAppQuery(
    ['dbsSelectableProperties', agencyUid],
    async () => {
      const response = await API.get<SelectableFeaturedProperty[]>(
        `${DBS_SETTINGS_URL}/selectable-properties`,
        { params: { agencyUid } },
      );
      return getPropertyOptions(response.data);
    },
    { enabled: !!agencyUid },
  );

  return {
    propertyOptions: data ?? [],
    isLoading: isInitialLoading,
    isError,
  };
};

export default useFetchSelectablePropertyOptions;
